import React, { useState, useEffect } from "react";
import BaseTailoringQuestions from "../shared/BaseTailoringQuestions";
import type { TailoringQuestion } from "../shared/BaseTailoringQuestions";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { AlertCircle, FileText, FileSignature } from "lucide-react";
import complianceData from "../../constants/ConcludingProcedures/Compliance.json";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface ComplianceWithLawsAndRegulationsLandingProps {
  onSelect?: (section: string) => void;
}

// Transform JSON structure to TailoringQuestion format
function transformJSONToQuestions(jsonData: any): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];
  const sections = jsonData.sections || [];

  // Helper to create a question
  const createQuestion = (
    id: string,
    questionText: string,
    type: "radio" | "text" | "table" | "dropdown",
    options?: string[],
    followUp?: TailoringQuestion["follow_up"],
    tableConfig?: TailoringQuestion["tableConfig"],
    subItems?: TailoringQuestion["subItems"],
    hasDocumentManager?: boolean,
    guidance?: TailoringQuestion["guidance"]
  ): TailoringQuestion => {
    return {
      id,
      question: questionText,
      type,
      options,
      follow_up: followUp,
      tableConfig,
      subItems,
      hasDocumentManager,
      guidance,
    };
  };

  sections.forEach((section: any, sectionIndex: number) => {
    // Handle table sections
    if (section.type === "table" && section.columns) {
      const tableColumns = section.columns.map((col: any) => {
        let colType = "text";
        let colOptions: string[] | undefined = undefined;

        if (col.type === "dropdown" && col.options) {
          colType = "dropdown";
          colOptions = col.options;
        } else if (col.type === "textarea") {
          colType = "textarea";
        }

        return {
          key: col.key,
          label: col.label,
          type: colType,
          options: colOptions,
        };
      });

      questions.push(
        createQuestion(
          `CLR-${sectionIndex + 1}`,
          section.description || section.title || "Enter information in the table below:",
          "table",
          undefined,
          undefined,
          {
            columns: tableColumns,
            allowAdd: section.allowAddRows !== false,
            allowDelete: true,
          }
        )
      );
    }

    // Handle sections with items
    if (section.items && Array.isArray(section.items)) {
      let questionIdPrefix = `CLR-${sectionIndex + 1}`;
      
      // Handle numbered sections (with section.text and section.number)
      // These should be grouped as one question with subItems
      if (section.text && section.number) {
        // Collect all items with roman numerals as subItems (excluding [Document] marker)
        const subItems: Array<{ id: string; number?: string; text: string; type: string }> = [];
        let hasDocumentMarker = false;
        
        section.items.forEach((item: any, itemIndex: number) => {
          if (item.type === "textarea") {
            if (item.text === "[Document]") {
              // [Document] is just a marker - don't add it as a subItem
              hasDocumentMarker = true;
            } else {
              subItems.push({
                id: `${questionIdPrefix}-sub-${itemIndex + 1}`,
                number: item.number,
                text: item.text,
                type: "text"
              });
            }
          }
        });
        
        // Create one main question with all subItems
        const mainQuestion = createQuestion(
          questionIdPrefix,
          `${section.number}. ${section.text}`,
          "text"
        );
        mainQuestion.subItems = subItems;
        mainQuestion.hasDocumentManager = hasDocumentMarker; // Mark that this question needs a DocumentManager
        questions.push(mainQuestion);
      } else if (section.text) {
        // Handle sections with text but no number (legacy format)
        // For each item, combine section.text with item text
        section.items.forEach((item: any, itemIndex: number) => {
          if (item.type === "textarea") {
            let questionText: string;
            if (item.text === "[Document]") {
              questionText = section.text;
            } else if (item.number) {
              questionText = `${section.text}\n\n${item.number}. ${item.text}`;
            } else {
              questionText = `${section.text}\n\n${item.text}`;
            }
            
            questions.push(
              createQuestion(
                `${questionIdPrefix}-${itemIndex + 1}`,
                questionText,
                "text"
              )
            );
          }
        });
      } else if (section.title && section.items.some((item: any) => item.number && item.type === "textarea")) {
        // Handle sections with title and items that have numbers (roman numerals) - group as one question
        const subItems: Array<{ id: string; number?: string; text: string; type: string }> = [];
        let hasDocumentMarker = false;
        
        section.items.forEach((item: any, itemIndex: number) => {
          if (item.type === "textarea") {
            if (item.text === "[Document]") {
              // [Document] is just a marker - don't add it as a subItem
              hasDocumentMarker = true;
            } else {
              subItems.push({
                id: `${questionIdPrefix}-sub-${itemIndex + 1}`,
                number: item.number,
                text: item.text,
                type: "text"
              });
            }
          }
        });
        
        if (subItems.length > 0) {
          // Create one main question with all subItems
          const mainQuestion = createQuestion(
            questionIdPrefix,
            section.title,
            "text",
            undefined,
            undefined,
            undefined,
            subItems
          );
          mainQuestion.hasDocumentManager = hasDocumentMarker; // Mark that this question needs a DocumentManager
          questions.push(mainQuestion);
        }
      } else {
        // Handle sections without section.text (like title-based sections)
        section.items.forEach((item: any, itemIndex: number) => {
          // Skip instruction type items - they're handled in follow-ups
          if (item.type === "instruction") {
            return;
          }
          
          if (item.type === "textarea") {
            // Handle [Document] placeholder
            const questionText = item.text === "[Document]" 
              ? (section.title || "Document:")
              : (item.number ? `${item.number}. ${item.text}` : item.text);
            
            questions.push(
              createQuestion(
                `${questionIdPrefix}-${itemIndex + 1}`,
                questionText,
                "text"
              )
            );
          } else if (item.type === "radio") {
            const questionText = item.number 
              ? `${item.number}. ${item.text.replace(" [Yes/No]", "")}`
              : item.text.replace(" [Yes/No]", "");
            
            // Check for conditional table
            let followUp: TailoringQuestion["follow_up"] | undefined = undefined;
            
            if (section.conditionalTable) {
              const conditionalTable = section.conditionalTable;
              const tableColumns = conditionalTable.columns.map((col: any) => {
                let colType = "text";
                if (col.type === "textarea") {
                  colType = "textarea";
                } else if (col.type === "dropdown" && col.options) {
                  colType = "dropdown";
                }

                return {
                  key: col.key,
                  label: col.label,
                  type: colType,
                  options: col.options,
                };
              });

              // Find instruction text if available (but don't use it as question text - table will appear directly)
              const instructionItem = section.items.find((i: any) => i.type === "instruction");

              followUp = {
                id: `${questionIdPrefix}-${itemIndex + 1}-table`,
                question: "", // Empty question text - table will appear directly without a label
                type: "table",
                condition: "Yes",
                tableConfig: {
                  columns: tableColumns,
                  allowAdd: conditionalTable.allowAddRows !== false,
                  allowDelete: true,
                },
              };
            } else {
              // Check for "Other response" option follow-up with DocumentManager
              const otherItem = section.items.find(
                (i: any) => i.visibleWhen === "Other response" && i.text === "[Document]"
              );
              
              if (otherItem) {
                followUp = {
                  id: `${questionIdPrefix}-${itemIndex + 1}-other`,
                  question: "Document:",
                  type: "document",
                  condition: "Other response",
                };
              }
            }

            // Check for guidance section
            const guidance = section.guidance ? {
              text: section.guidance.text,
              items: section.guidance.items
            } : undefined;

            questions.push(
              createQuestion(
                `${questionIdPrefix}-${itemIndex + 1}`,
                questionText,
                "radio",
                item.options || [],
                followUp,
                undefined,
                undefined,
                undefined,
                guidance
              )
            );
          } else if (item.type === "dropdown") {
            const dropdownOptions = item.options || [];
            let followUp: TailoringQuestion["follow_up"] | undefined = undefined;

            // Check for "Other response" option follow-up
            const otherItem = section.items.find(
              (i: any) => i.visibleWhen === "Other response"
            );
            
            if (otherItem) {
              followUp = {
                id: `${questionIdPrefix}-${itemIndex + 1}-other`,
                question: otherItem.text || "Please provide details:",
                type: "text",
                condition: "Other response",
              };
            }

            questions.push(
              createQuestion(
                `${questionIdPrefix}-${itemIndex + 1}`,
                item.text || "Select an option:",
                "dropdown",
                dropdownOptions,
                followUp
              )
            );
          }
          // Skip instruction type items as they're handled in follow-ups
        });
      }
    }

    // Guidance sections are now attached to questions, not separate questions

  });

  return questions;
}

const WORKSPACE_NAME = "Compliance with laws and regulations";

export default function ComplianceWithLawsAndRegulationsLanding(_props: ComplianceWithLawsAndRegulationsLandingProps) {
  const [questions, setQuestions] = useState<TailoringQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        const jsonData = complianceData;
        const transformedQuestions = transformJSONToQuestions(jsonData);
        setQuestions(transformedQuestions);
      } catch (err) {
        console.error("Failed to load questions:", err);
        setError(err instanceof Error ? err.message : "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const m = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(m?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    load();
  }, [currentEngagement, user]);

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Compliance with laws and regulations</h2>
          <p className="text-white/60">Loading questions...</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions from JSON file...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Compliance with laws and regulations</h2>
          <p className="text-white/60">Error loading questions</p>
        </div>
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Compliance with laws and regulations</h2>
          <p className="text-white/60">No questions found</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <FileText className="h-8 w-8 mx-auto mb-4 text-white/40" />
              <p>No questions available. Please check the JSON file.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 flex-wrap px-6 pt-2">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button variant="outline" size="sm" onClick={() => setSignoffDialogOpen(true)} className="border-white/20 text-white hover:bg-white/10">
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <BaseTailoringQuestions
        areaName="CLR"
        title="Compliance with laws and regulations"
        description="Answer the following questions based on compliance with laws and regulations requirements."
        questions={questions}
        onComplete={(answers) => {
          console.log("Compliance with laws and regulations answers:", answers);
        }}
      />
    </div>
  );
}
